import React, { type ReactNode } from 'react'
import { View } from 'react-native'

import { twMerge } from '../../tailwind/merge'
import { modalFooterContract } from './modal-footer.contract'

const VARIANTS = modalFooterContract.props.variant.values

export type ModalFooterVariant = (typeof VARIANTS)[number]

export type ModalFooterProps = {
    'variant'?: ModalFooterVariant
    /** Figma's `slot` — the leading edge, empty in every sample. */
    'slot'?: ReactNode
    /** Figma's `slot-primary` — the buttons, pushed to the trailing edge. */
    'slot-primary'?: ReactNode
    'className'?: string
    'testID'?: string
}

/**
 * The bottom of a modal, and the mirror of `ModalHeader`: it rounds its bottom
 * corners and strokes its top edge, where the header rounds the top and strokes
 * the bottom. That pair is the seam that makes three components read as one
 * card.
 *
 * The variant is which `Button` size the caller is expected to put in
 * `slot-primary` — `modalFooterContract.variants[key].composes` names it, and
 * the spec holds it against Button's own contract.
 *
 * Figma draws the footer 860px wide, which is the sample modal rather than a
 * constraint; a caller passes `w-full` through `className`, as `ListItem` does.
 * That is on the list for design.
 */
const variantFor = (variant: ModalFooterVariant) => {
    const key = `variant=${variant}`
    const found = (modalFooterContract.variants as Record<string, { container: string }>)[key]
    if (!found) {
        throw new Error(`aos-ds ModalFooter: the Figma contract has no "${key}"`)
    }
    return found
}

export const ModalFooter = ({
    variant = 'big-buttons',
    slot,
    'slot-primary': slotPrimary,
    className,
    testID,
}: ModalFooterProps) => (
    <View
        className={twMerge('flex-row items-center', variantFor(variant).container, className)}
        testID={testID ?? `modal-footer-${variant}`}
    >
        <View className='flex-1' testID='modal-footer-slot'>
            {slot}
        </View>
        <View className='flex-row items-center gap-m' testID='modal-footer-slot-primary'>
            {slotPrimary}
        </View>
    </View>
)

ModalFooter.displayName = 'ModalFooter'

export { VARIANTS as MODAL_FOOTER_VARIANTS }
